/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  UserFinderDialogConfig,
  userFinderDialogRoute,
  UserFinderListItem,
} from '@gh/builder-shared';
import { SystemRoleBit, systemRolesBitSet } from '@gh/builder-shared-data';
import { ActionFlow, getAllNewSelectedEntities, queryFormMode, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  DialogService,
  FormMode,
  NotificationService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';
import { SalespersonBudgetListBusinessService } from '../../resources/salesperson-budget-list-business.service';
import { sbItemFromUser, SbListItem } from '../../resources/sb-list.model';
import {
  querySbFilter,
  querySbHasErrors,
  querySbItemsByGroup,
  querySbOriginalItems,
  querySbSaveModel,
  SbListState,
  SbListStateEventType,
} from './sb-list.state';

export type SbSaveFlowState = FormFlowState<SbListState> & { saveResponse: SbListItem[] };

@Injectable()
export class SbListCommandService {
  constructor(private state: State<SbListState>,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private businessService: SalespersonBudgetListBusinessService,
              private dialogService: DialogService,
              private notificationService: NotificationService) {

  }

  load(items: SbListItem[], mode: FormMode): void {
    this.state.mutate({ type: SbListStateEventType.Load, items, mode });
  }

  setFilterProperty(name: string, value: any): void {
    this.state.mutate({ type: SbListStateEventType.FilterSetField, name, value });
  }

  addItems(): void {
    const { type, year } = this.state.project(querySbFilter);

    this.dialogService.openWithInput(
      userFinderDialogRoute,
      <UserFinderDialogConfig>{
        multiselection: true,
        filter: {
          showInactive: false,
          systemRoles: systemRolesBitSet(SystemRoleBit.SalesPerson),
        },
        preselected: this.state.project((s) => querySbItemsByGroup(s, type, year)).map((item) => item.salespersonId),
      })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      map$((users: UserFinderListItem[]) => users.map((user) => sbItemFromUser(user, type, year))))
      .subscribe((items) => this.state.mutate({ type: SbListStateEventType.ItemAddSet, items }));
  }

  removeItem(itemId: ID): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_salesperson_budgets_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.state.mutate({ type: SbListStateEventType.ItemRemove, itemId }));
  }

  setItemField(itemId: ID, name: string, value: any): void {
    this.state.mutate({ type: SbListStateEventType.ItemSetField, itemId, name, value });
  }

  resetSelectedItem(): void {
    this.state.mutate({ type: SbListStateEventType.ResetSelectedId });
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: SbListStateEventType.ResetFocusedCell });
  }

  reset(): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.state.mutate({
        type: SbListStateEventType.Load,
        items: this.state.project(querySbOriginalItems),
        mode: this.state.project(queryFormMode),
      }));
  }

  completeFlow(): ActionFlow<SbSaveFlowState> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        (state: SbSaveFlowState) => this.state.project(querySbHasErrors),
        (state: SbSaveFlowState) => this.handleErrors()),
      execute: this.commonActions.async((state: SbSaveFlowState) =>
        this.businessService.update(this.state.project(querySbSaveModel)).pipe(
          map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: ({ saveResponse }: SbSaveFlowState) => {
        this.state.mutate({
          type: SbListStateEventType.Load,
          items: saveResponse,
          mode: this.state.project(queryFormMode),
        });
      },
    });
  }

  private handleErrors(): void {
    this.notificationService.error('msg_he_incorrect_data_in_tables');
    this.state.mutate({ type: SbListStateEventType.NavigateToInvalid });
  }
}
